"use client";

import { useState, useCallback, useEffect } from "react";
import {
  useAppKit,
  useAppKitAccount,
  useAppKitProvider,
  useDisconnect,
} from "@reown/appkit/react";
import { BrowserProvider } from "ethers";
import type { Provider } from "@reown/appkit-adapter-ethers";

export type ConnectionStatus =
  | "disconnected"
  | "connecting"
  | "connected"
  | "signing"
  | "signed"
  | "error";

export interface SignatureResult {
  signature: string;
  message: string;
}

interface UseDevnetWalletConnectReturn {
  // State
  status: ConnectionStatus;
  address: string | null;
  error: string | null;

  // Actions
  openConnectModal: () => void;
  disconnect: () => Promise<void>;
  signAndLink: (
    portalUserAddress: string,
    buildMessage?: (linkedWalletAddress: string, portalUserAddress: string, timestamp: number) => string,
  ) => Promise<SignatureResult | null>;
  reset: () => void;
}

/**
 * Hook for connecting an external wallet and signing a message to prove ownership.
 * Used by email users to link their devnet wallet.
 */
export function useDevnetWalletConnect(): UseDevnetWalletConnectReturn {
  const { open } = useAppKit();
  const { address: connectedAddress, isConnected } = useAppKitAccount();
  const { walletProvider } = useAppKitProvider<Provider>("eip155");
  const { disconnect: appKitDisconnect } = useDisconnect();

  const [status, setStatus] = useState<ConnectionStatus>("disconnected");
  const [error, setError] = useState<string | null>(null);
  const [internalAddress, setInternalAddress] = useState<string | null>(null);

  // Sync connected address from AppKit
  useEffect(() => {
    if (isConnected && connectedAddress) {
      setInternalAddress(connectedAddress);
      if (status === "connecting" || status === "disconnected") {
        setStatus("connected");
      }
    } else if (!isConnected && status !== "disconnected" && status !== "signed") {
      // User disconnected from modal
      setInternalAddress(null);
      if (status !== "error") {
        setStatus("disconnected");
      }
    }
  }, [isConnected, connectedAddress, status]);

  const openConnectModal = useCallback(() => {
    setError(null);
    setStatus("connecting");
    open({ view: "Connect" });
  }, [open]);

  const disconnect = useCallback(async () => {
    try {
      await appKitDisconnect();
      setInternalAddress(null);
      setStatus("disconnected");
      setError(null);
    } catch (err) {
      console.error("Failed to disconnect:", err);
      // Force reset state even if disconnect fails
      setInternalAddress(null);
      setStatus("disconnected");
    }
  }, [appKitDisconnect]);

  const signAndLink = useCallback(
    async (
      portalUserAddress: string,
      buildMessage?: (linkedWalletAddress: string, portalUserAddress: string, timestamp: number) => string,
    ): Promise<SignatureResult | null> => {
      if (!walletProvider || !internalAddress) {
        setError("No wallet connected");
        setStatus("error");
        return null;
      }

      if (!portalUserAddress) {
        setError("Portal user address is required");
        setStatus("error");
        return null;
      }

      setStatus("signing");
      setError(null);

      try {
        const provider = new BrowserProvider(walletProvider);
        const signer = await provider.getSigner();

        // Create message with timestamp (must be within 5 minutes of server time)
        const timestamp = Date.now();
        const message = buildMessage
          ? buildMessage(internalAddress, portalUserAddress, timestamp)
          : `I confirm linking wallet ${internalAddress} to Spicenet account ${portalUserAddress}.\n\nTimestamp: ${timestamp}\n\nThis signature proves ownership of this wallet address.`;

        const signature = await signer.signMessage(message);

        setStatus("signed");
        return { signature, message };
      } catch (err) {
        console.error("Signing failed:", err);

        // Handle user rejection
        if (
          err instanceof Error &&
          (err.message.includes("rejected") ||
            err.message.includes("denied") ||
            err.message.includes("cancelled") ||
            err.message.includes("user rejected"))
        ) {
          setError("Signature request was rejected");
        } else {
          setError("Failed to sign message. Please try again.");
        }

        setStatus("error");
        return null;
      }
    },
    [walletProvider, internalAddress]
  );

  const reset = useCallback(() => {
    setStatus("disconnected");
    setError(null);
    setInternalAddress(null);
    // Also disconnect if connected
    if (isConnected) {
      appKitDisconnect().catch(console.error);
    }
  }, [isConnected, appKitDisconnect]);

  return {
    status,
    address: internalAddress,
    error,
    openConnectModal,
    disconnect,
    signAndLink,
    reset,
  };
}
